import { faGrid, faTableLayout, faXmark } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { type Theme, useTheme } from "@mui/material";
import { type FC, useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";

import type { ExperienceSettingsFormValues } from "@/VisualBuilder/components/ExperienceSettingsDialog/ExperienceSettingsModal";
import * as S from "@/VisualBuilder/components/ExperienceSettingsDialog/styled";
import { ROLES } from "@/constants";
import { selectUser } from "@/store/auth/auth.slice";
import { useThunkSelector } from "@/store/hooks";
import { currentExperienceSelector } from "@/store/v2/currentExperience/selectors";

interface ExperienceSettingsTitleProps {
  readonly onCloseDialog: () => void;
}

const ExperienceSettingsTitle: FC<ExperienceSettingsTitleProps> = ({ onCloseDialog }) => {
  const theme = useTheme<Theme>();

  const { experience } = useSelector(currentExperienceSelector);
  const user = useThunkSelector(selectUser);

  const isSalesperson = !!user && user.userRole.title === ROLES.SALESPERSON;

  const { isTemplate } = experience;
  const isCollection = experience.userCollection === true;

  const { control, setValue } = useFormContext<ExperienceSettingsFormValues>();

  useEffect(() => {
    if (isSalesperson) setValue("locked", true);
  }, [isSalesperson]);

  return (
    <S.DialogTitle>
      <S.DialogOuterContainer>
        <S.DialogTitleContainer>
          <S.DialogTitleText>Experience Settings</S.DialogTitleText>
          {isTemplate && (
            <S.DialogTitleChip
              $theme={theme}
              icon={<FontAwesomeIcon icon={faTableLayout} color="black" />}
              label="Template"
              size="small"
            />
          )}
          {isCollection && (
            <S.DialogTitleChip
              $theme={theme}
              icon={<FontAwesomeIcon icon={faGrid} color="black" />}
              label="My Collection"
              size="small"
            />
          )}
          <S.DialogTitleStatus>{experience.status}</S.DialogTitleStatus>
        </S.DialogTitleContainer>
        <S.CloseIconButton $theme={theme} onClick={onCloseDialog}>
          <FontAwesomeIcon icon={faXmark} fontSize="18px" color={theme.palette.semanticNeutral.dark} />
        </S.CloseIconButton>
      </S.DialogOuterContainer>

      <Controller
        name="locked"
        control={control}
        render={({ field }) => (
          <S.FormControlLabel
            $theme={theme}
            control={
              <S.FormSwitch
                $theme={theme}
                disabled={isSalesperson}
                checked={!field.value}
                disableRipple
                onChange={(event) => {
                  field.onChange(!event.target.checked);
                }}
              />
            }
            label={field.value ? "Locked" : "Unlocked"}
          />
        )}
      />
    </S.DialogTitle>
  );
};

export default ExperienceSettingsTitle;
